import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Topic } from '../types/index.ts';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';

import RatingModal from './RatingModal.tsx';
import EditTopicModal from './EditTopicModal.tsx';
import MeetingPrepModal from './MeetingPrepModal.tsx';
import ShareFeedbackModal from './ShareFeedbackModal.tsx';
import TruncatedDescription from './TruncatedDescription.tsx';
import PresentersList from './PresentersList.tsx';
import ScobyIcon from './icons/ScobyIcon.tsx';
import GitlabIcon from './icons/GitlabIcon.tsx';

interface TopicRowProps {
  topic: Topic;
  onTopicUpdate: () => void;
  sessionSettings?: { sessionDate: string; sessionTime: string } | null;
  showAllColumns?: boolean; // New prop to show all columns for past topics page
  showAISummary?: boolean; // New prop to show AI summary icon only on past topics page
  showDuration?: boolean; // Show duration column for scheduled topics
  showEstimatedDate?: boolean; // Show estimated presentation date column for future topics
  autoOpenFeedback?: boolean; // New prop to auto-open feedback modal
  onAutoOpenFeedbackCleared?: () => void; // Callback to clear auto-open state
  index?: number; // Row index for smart dropdown positioning
  totalCount?: number; // Total number of rows for smart dropdown positioning
  showDropdown?: boolean; // External dropdown state control
  onDropdownToggle?: (topicId: string) => void; // External dropdown toggle handler
}

const StarRating: React.FC<{ rating: number | undefined; onClick?: () => void }> = ({ rating, onClick }) => {
  const hasInteractivity = !!onClick;
  
  if (!rating && hasInteractivity) {
    // Show 5 gray stars when no rating and interactive
    const grayStars = [];
    for (let i = 0; i < 5; i++) {
      grayStars.push(<span key={i} className="star gray">☆</span>);
    }
    return (
      <div 
        className="star-rating" 
        onClick={onClick} 
        title="Submit your feedback"
        style={{ cursor: 'pointer', color: '#999' }}
      >
        {grayStars}
      </div>
    );
  }
  
  if (!rating) return <span className="no-rating" onClick={onClick} title={onClick ? "Submit your feedback" : undefined} style={{ cursor: onClick ? 'pointer' : 'default' }}>-</span>;
  
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  for (let i = 0; i < fullStars; i++) {
    stars.push(<span key={i} className="star full">★</span>);
  }
  
  if (hasHalfStar) {
    stars.push(<span key="half" className="star half">☆</span>);
  }
  
  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<span key={`empty-${i}`} className="star empty">☆</span>);
  }
  
  return (
    <div 
      className="star-rating" 
      onClick={onClick} 
      title={onClick ? "Submit your feedback" : undefined}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {stars}
    </div>
  );
};

const CustomerCount: React.FC<{ count: number | undefined; onClick?: () => void }> = ({ count, onClick }) => {
  const hasInteractivity = !!onClick;
  
  if ((!count || count === 0) && hasInteractivity) {
    // Show 0 with gray background when no customers and interactive
    return (
      <span 
        className="customer-count zero-count" 
        onClick={onClick} 
        title="Submit your feedback"
        style={{ 
          cursor: 'pointer',
          backgroundColor: '#f0f0f0',
          color: '#666',
          padding: '2px 6px',
          borderRadius: '3px',
          border: '1px solid #ccc'
        }}
      >
        0
      </span>
    );
  }
  
  if (!count) return <span className="no-count" onClick={onClick} title={onClick ? "Submit your feedback" : undefined} style={{ cursor: onClick ? 'pointer' : 'default' }}>-</span>;
  
  return (
    <span 
      className="customer-count" 
      onClick={onClick} 
      title={onClick ? "Submit your feedback" : undefined}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {count}
    </span>
  );
};

const HoursSaved: React.FC<{ hours: number | undefined; onClick?: () => void }> = ({ hours, onClick }) => {
  const hasInteractivity = !!onClick;
  
  if ((!hours || hours === 0) && hasInteractivity) {
    // Show 0 with gray background when no hours and interactive (like customers)
    return (
      <span 
        className="hours-saved zero-hours" 
        onClick={onClick} 
        title="Submit your feedback"
        style={{ 
          cursor: 'pointer',
          backgroundColor: '#f0f0f0',
          color: '#666',
          padding: '2px 6px',
          borderRadius: '3px',
          border: '1px solid #ccc'
        }}
      >
        0
      </span>
    );
  }
  
  if (!hours) return <span className="no-hours" onClick={onClick} title={onClick ? "Submit your feedback" : undefined} style={{ cursor: onClick ? 'pointer' : 'default' }}>-</span>;
  
  return (
    <span 
      className="hours-saved" 
      onClick={onClick} 
      title={onClick ? "Submit your feedback" : undefined}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: hasInteractivity ? '#e3f2fd' : 'transparent',
        color: '#1976d2',
        fontWeight: '500',
        padding: hasInteractivity ? '2px 6px' : '0',
        borderRadius: hasInteractivity ? '3px' : '0',
        border: hasInteractivity ? '1px solid #90caf9' : 'none'
      }}
    >
      {hours}
    </span>
  );
};

const TopicRow: React.FC<TopicRowProps> = ({ topic, onTopicUpdate, sessionSettings, showAllColumns = false, showAISummary = false, showDuration = false, showEstimatedDate = false, autoOpenFeedback = false, onAutoOpenFeedbackCleared, index = 0, totalCount = 0, showDropdown: externalShowDropdown, onDropdownToggle }) => {
  const [attendeeCount, setAttendeeCount] = useState(topic.attendeeCount.toString());
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Use external dropdown state if provided, otherwise use internal state
  const isDropdownOpen = externalShowDropdown !== undefined ? externalShowDropdown : showDropdown;
  const toggleDropdown = onDropdownToggle ? () => onDropdownToggle(topic.topicId) : () => setShowDropdown(!showDropdown);
  const [showMeetingPrepModal, setShowMeetingPrepModal] = useState(false);
  const [showShareFeedbackModal, setShowShareFeedbackModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMarkPresentedConfirm, setShowMarkPresentedConfirm] = useState(false);
  const [presentedDate, setPresentedDate] = useState('');
  const [markPresentedError, setMarkPresentedError] = useState('');
  const [isMarkingPresented, setIsMarkingPresented] = useState(false);
  useEffect(() => {
    setAttendeeCount(topic.attendeeCount.toString());
  }, [topic.attendeeCount]);

  useEffect(() => {
    // Auto-open feedback modal if requested
    if (autoOpenFeedback) {
      console.log('Auto-opening feedback modal for topic:', topic.title);
      setShowRatingModal(true);
    }
  }, [autoOpenFeedback]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && !onDropdownToggle) { // Only handle internally if no external handler
        setShowDropdown(false);
      }
    };

    if (showDropdown && !onDropdownToggle) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown, onDropdownToggle]);

  // Helper function to determine if rating button and attendee input should be shown
  const shouldShowInteractiveElements = () => {
    // Always show for past topics and next session topics
    return topic.sessionType === 'past' || topic.sessionType === 'next';
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteTopic = async () => {
    try {
      setIsDeleting(true);
      await topicApi.deleteTopic(topic.topicId);
      onTopicUpdate();
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMarkAsPresented = async () => {
    if (!presentedDate) {
      setMarkPresentedError('Please select a presentation date');
      return;
    }

    // Validate that the date is not in the future
    const selectedDate = new Date(presentedDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    
    if (selectedDate > today) {
      setMarkPresentedError('Presentation date cannot be in the future');
      return;
    }

    setMarkPresentedError('');

    try {
      setIsMarkingPresented(true);
      // Convert selected date to ISO string (use noon UTC to avoid timezone issues)
      const selectedDateTime = new Date(presentedDate + 'T12:00:00.000Z');
      
      // Only send the fields that need to change - don't touch presenters!
      await topicApi.updateTopic(topic.topicId, {
        sessionType: 'past',
        presentedAt: selectedDateTime.toISOString()
      });
      onTopicUpdate();
      setShowMarkPresentedConfirm(false);
      setPresentedDate(''); // Reset for next time
    } catch (error) {
      console.error('Error marking topic as presented:', error);
      setMarkPresentedError('Failed to mark topic as presented. Please try again.');
    } finally {
      setIsMarkingPresented(false);
    }
  };

  // Get default presentation date (session date or current date)
  const getDefaultPresentationDate = () => {
    if (sessionSettings?.sessionDate) {
      return sessionSettings.sessionDate; // Already in YYYY-MM-DD format
    }
    return new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD
  };

  const handleAttendeeCountChange = (value: string) => {
    setAttendeeCount(value);
  };

  const handleBlur = () => {
    saveAttendeeCount(parseInt(attendeeCount) || 0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAttendeeCount(parseInt(attendeeCount) || 0);
    }
  };

  const saveAttendeeCount = async (count: number) => {
    if (count === topic.attendeeCount) return;
    
    setIsUpdating(true);
    try {
      await topicApi.updateAttendeeCount(topic.topicId, count);
      onTopicUpdate();
    } catch (error) {
      console.error('Failed to update attendee count:', error);
      setAttendeeCount(topic.attendeeCount.toString());
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <tr className="topic-row">
        <td className="topic-title" style={{ minWidth: '350px' }}>
          <div className="topic-title" style={{ maxWidth: 'none' }}>{topic.title}</div>
          <div className="topic-description" style={{ marginLeft: '16px' }}>
            <TruncatedDescription description={topic.description} topicId={topic.topicId} />
          </div>
        </td>
        <td className="presenter-cell">
          <PresentersList 
            presenters={topic.presenters || []} 
            currentUser={authService.getUserInfo()?.username}
            displayMode="rows"
          />
        </td>
        {showEstimatedDate && (
          <td className="estimated-date-cell">
            {topic.estimatedPresentationDate ? new Date(topic.estimatedPresentationDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
          </td>
        )}
        {showDuration && (
          <td className="duration-cell">
            {topic.duration ? `${topic.duration} min` : '-'}
          </td>
        )}
        {showAllColumns && (
          <td className="presented-date-cell">
            {topic.presentedAt ? new Date(topic.presentedAt).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric' 
            }) : '-'}
          </td>
        )}
        {showAllColumns && (
          <td className="team-cell">{topic.teamName || 'Unknown Team'}</td>
        )}
        <td className="attendee-cell">
          {shouldShowInteractiveElements() ? (
            <>
              <input
                type="number"
                value={attendeeCount}
                onChange={(e) => handleAttendeeCountChange(e.target.value)}
                onBlur={handleBlur}
                onKeyPress={handleKeyPress}
                min="0"
                className={`attendee-input ${isUpdating ? 'updating' : ''}`}
              />
              {isUpdating && <span className="saving-indicator">💾</span>}
            </>
          ) : (
            <span className="disabled-input">-</span>
          )}
        </td>
        <td className="rating-cell">
          <StarRating 
            rating={topic.averageUsefulnessRating} 
            onClick={shouldShowInteractiveElements() ? () => setShowRatingModal(true) : undefined}
          />
        </td>
        <td className="hours-cell">
          <HoursSaved 
            hours={topic.totalHoursSaved} 
            onClick={shouldShowInteractiveElements() ? () => setShowRatingModal(true) : undefined}
          />
        </td>
        <td className="customers-cell">
          <CustomerCount 
            count={topic.totalCustomerConversations} 
            onClick={shouldShowInteractiveElements() ? () => setShowRatingModal(true) : undefined}
          />
        </td>
        <td className="edit-cell">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
            {/* Scoby icon - only show if URL exists and is not empty */}
            {topic.builderSpaceUrl && topic.builderSpaceUrl.trim() && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(topic.builderSpaceUrl, '_blank');
                }}
                className="action-icon-button"
                title="Scoby Project"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                <ScobyIcon />
              </button>
            )}

            {/* Gitlab icon - only show if URL exists and is not empty */}
            {topic.gitlabUrl && topic.gitlabUrl.trim() && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(topic.gitlabUrl, '_blank');
                }}
                className="action-icon-button"
                title="Gitlab Project"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                <GitlabIcon />
              </button>
            )}

            {/* Recording icon - only show if recording URL exists and is not empty */}
            {topic.recordingUrl && topic.recordingUrl.trim() && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(topic.recordingUrl, '_blank');
                }}
                className="action-icon-button"
                title="Recording"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ▶
              </button>
            )}
            
            {/* AI Summary icon - dummy for now, only show on past topics page */}
            {showAISummary && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMeetingPrepModal(true);
                }}
                className="action-icon-button"
                title="Generate meeting prep notes"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ✦
              </button>
            )}
            
            {/* Existing 3-dots menu */}
            <div style={{ position: 'relative' }}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDropdown();
                }}
              className="dropdown-button"
              title="Actions"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
            >
              ⋮
            </button>
            {isDropdownOpen && (
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: index >= totalCount - 2 ? 'auto' : '100%',
                  bottom: index >= totalCount - 2 ? '100%' : 'auto',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 9999,
                  minWidth: '200px'
                }}
              >
              <button
                onClick={() => {
                  setShowEditModal(true);
                  if (onDropdownToggle) {
                    onDropdownToggle(topic.topicId);
                  } else {
                    setShowDropdown(false);
                  }
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                Edit
              </button>
              {topic.sessionType !== 'future' && (
                <button
                  onClick={() => {
                    setShowShareFeedbackModal(true);
                    if (onDropdownToggle) {
                      onDropdownToggle(topic.topicId);
                    } else {
                      setShowDropdown(false);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                >
                  Share feedback link
                </button>
              )}
              {(topic.sessionType === 'next' || topic.sessionType === 'future') && (
                <button
                  onClick={() => {
                    setPresentedDate(getDefaultPresentationDate()); // Set default date when opening modal
                    setShowMarkPresentedConfirm(true);
                    if (onDropdownToggle) {
                      onDropdownToggle(topic.topicId);
                    } else {
                      setShowDropdown(false);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                >
                  Mark as Presented
                </button>
              )}
              <button
                onClick={() => {
                  setShowDeleteConfirm(true);
                  if (onDropdownToggle) {
                    onDropdownToggle(topic.topicId);
                  } else {
                    setShowDropdown(false);
                  }
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#dc3545'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                Delete Topic
              </button>
            </div>
          )}
        </div>
          </div>
        </td>
      </tr>

      {/* Render modals using React Portal to avoid DOM nesting issues */}
      {showRatingModal && ReactDOM.createPortal(
        <RatingModal
          topic={topic}
          onClose={() => {
            console.log('RatingModal onClose called');
            setShowRatingModal(false);
            if (onAutoOpenFeedbackCleared) {
              console.log('Calling onAutoOpenFeedbackCleared');
              onAutoOpenFeedbackCleared();
            }
          }}
          onRatingSubmitted={() => {
            console.log('RatingModal onRatingSubmitted called');
            onTopicUpdate();
            // Also clear auto-open state when rating is submitted
            if (onAutoOpenFeedbackCleared) {
              console.log('Calling onAutoOpenFeedbackCleared after submit');
              onAutoOpenFeedbackCleared();
            }
          }}
        />,
        document.body
      )}

      {showEditModal && ReactDOM.createPortal(
        <EditTopicModal
          topic={topic}
          onClose={() => setShowEditModal(false)}
          onTopicUpdated={onTopicUpdate}
        />,
        document.body
      )}

      {showMeetingPrepModal && ReactDOM.createPortal(
        <MeetingPrepModal
          onClose={() => setShowMeetingPrepModal(false)}
        />,
        document.body
      )}

      {showShareFeedbackModal && ReactDOM.createPortal(
        <ShareFeedbackModal
          topic={topic}
          onClose={() => setShowShareFeedbackModal(false)}
        />,
        document.body
      )}

      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Topic</h3>
            <p>Are you sure you want to delete "{topic.title}"?</p>
            <p>This topic will be moved to the deleted topics page where it can be restored if needed.</p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button onClick={handleDeleteTopic} disabled={isDeleting} style={{ backgroundColor: isDeleting ? '#6c757d' : '#dc3545', color: 'white' }}>
                {isDeleting ? 'Deleting...' : 'Delete Topic'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showMarkPresentedConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowMarkPresentedConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Mark as Presented</h3>
            <p>Mark <strong style={{ color: '#333' }}>"{topic.title}"</strong> as presented and move to past topics.</p>
            
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <label htmlFor="presented-date" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Presentation Date:
              </label>
              <input
                id="presented-date"
                type="date"
                value={presentedDate}
                onChange={(e) => setPresentedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {sessionSettings?.sessionDate 
                  ? `Session date: ${new Date(sessionSettings.sessionDate + 'T00:00:00Z').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
                  : 'No session date set'}
              </div>
            </div>

            {markPresentedError && <div className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginTop: '12px' }}>{markPresentedError}</div>}

            <div className="modal-actions">
              <button onClick={() => {
                setShowMarkPresentedConfirm(false);
                setPresentedDate('');
                setMarkPresentedError('');
              }}>Cancel</button>
              <button onClick={handleMarkAsPresented} disabled={isMarkingPresented} style={{ backgroundColor: isMarkingPresented ? '#6c757d' : '#28a745', color: 'white' }}>
                {isMarkingPresented ? 'Marking...' : 'Mark as Presented'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TopicRow;
